import { APP_DISPLAY_NAME, PermissionCode } from '@gb-ticket/shared-types';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';

import { SkipLink } from '@/components/skip-link';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import {
  agentPathToPortal,
  isPortalAudience,
} from '@/products/helpdesk/features/portal/portal-paths';
import { isTicketListPath } from '@/products/helpdesk/features/tickets/ticket-list-path';
import { BrandAccentEffect } from '@/platform/brand/brand-accent-effect';
import { AppLogoLink } from '@/platform/components/app-logo-link';
import { PlatformChrome } from '@/platform/components/platform-chrome';
import '@/products/helpdesk/product-nav';
import '@/products/intranet/product-nav';
import '@/products/leave/product-nav';
import '@/products/fleet/product-nav';
import '@/products/maintenance/product-nav';
import '@/products/onboarding/product-nav';

export function RootLayout() {
  const me = useMe();
  const location = useLocation();

  if (me.data && isPortalAudience(me.data) && location.pathname !== '/anmelden') {
    const dest = agentPathToPortal(`${location.pathname}${location.search}`);
    if (dest) {
      return <Navigate to={dest} replace />;
    }
  }

  const isLogin = location.pathname === '/anmelden';
  const showAgentChrome = Boolean(me.data) && !isLogin;
  const showPortalLink = hasPermission(me.data, PermissionCode.TICKETS_READ_ALL);

  if (!showAgentChrome) {
    return (
      <div className="flex min-h-svh max-w-full min-w-0 flex-col">
        <BrandAccentEffect />
        <SkipLink />
        <header className="sticky top-0 z-40 border-b border-border bg-card">
          <div className="container mx-auto flex h-14 items-center gap-4 px-4 sm:h-16">
            <AppLogoLink
              title={
                <span className="leading-tight">
                  <span className="hidden sm:block">{APP_DISPLAY_NAME}</span>
                  <span className="sm:hidden">{APP_DISPLAY_NAME}</span>
                </span>
              }
            />
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Button asChild size="sm" variant="outline">
                <NavLink to="/anmelden">Anmelden</NavLink>
              </Button>
            </div>
          </div>
        </header>
        <main id="hauptinhalt" className="container mx-auto flex-1 px-4 py-8">
          <Outlet />
        </main>
        <footer className="border-t border-border py-4 text-center text-xs text-muted-foreground">
          {APP_DISPLAY_NAME}
        </footer>
        <Toaster />
      </div>
    );
  }

  return (
    <PlatformChrome
      brand={
        <AppLogoLink
          className="lg:hidden"
          title={<span className="text-sm font-semibold text-primary">Helpdesk</span>}
        />
      }
      mainClassName={isTicketListPath(location.pathname) ? 'py-4 md:py-5' : 'py-8'}
      userMenuProps={{ showPortalLink, showPortalHomeLink: true }}
      showFooter
    >
      <Outlet />
    </PlatformChrome>
  );
}
